// client/src/components/command/HistoryScanTab.jsx
// Triggers and monitors a history scan job that detects
// "locations were disabled for importing" in ShipHero order history.

import React, { useState, useCallback } from "react";
import { startHistoryScanJob } from "../../services/api";
import { useJob } from "../../hooks/useCommand";
import JobProgress from "./JobProgress";

const s = {
    container: {
        background: "#1a1f2e",
        borderRadius: "12px",
        border: "1px solid #1e293b",
        padding: "24px",
        marginBottom: "20px",
    },
    title: {
        fontSize: "16px",
        fontWeight: 700,
        color: "#e2e8f0",
        marginBottom: "8px",
    },
    description: {
        fontSize: "13px",
        color: "#94a3b8",
        marginBottom: "20px",
        lineHeight: "1.5",
    },
    button: (running) => ({
        background: running ? "#334155" : "linear-gradient(135deg, #8b5cf6, #6d28d9)",
        color: running ? "#94a3b8" : "#fff",
        border: "none",
        borderRadius: "8px",
        padding: "10px 24px",
        fontSize: "14px",
        fontWeight: 600,
        cursor: running ? "not-allowed" : "pointer",
        opacity: running ? 0.6 : 1,
    }),
    alert: {
        padding: "12px 16px",
        background: "#dc262615",
        border: "1px solid #dc262644",
        borderRadius: "8px",
        color: "#fca5a5",
        fontSize: "13px",
        marginTop: "16px",
    },
    info: {
        padding: "12px 16px",
        background: "#6366f115",
        border: "1px solid #6366f144",
        borderRadius: "8px",
        color: "#a5b4fc",
        fontSize: "13px",
        marginBottom: "16px",
        lineHeight: "1.5",
    },
};

export default function HistoryScanTab() {
    const [jobId, setJobId] = useState(null);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState(null);

    const { job } = useJob(jobId);
    const isRunning = job?.status === "running";

    const handleStart = useCallback(async () => {
        setStarting(true);
        setError(null);
        try {
            const data = await startHistoryScanJob();
            setJobId(data.jobId);
        } catch (err) {
            const msg = err?.response?.data?.error || err.message || "Failed to start";
            setError(msg);
            if (err?.response?.data?.jobId) {
                setJobId(err.response.data.jobId);
            }
        } finally {
            setStarting(false);
        }
    }, []);

    return (
        <div>
            <div style={s.container}>
                <div style={s.title}>🔍 History Scan — Location Disabled Detection</div>
                <div style={s.description}>
                    Scans all orders in the database by fetching their ShipHero order history.
                    Detects orders with the message <strong>"Order set as fulfilled because all its
                    locations were disabled for importing"</strong> — indicating a location change
                    or cancellation.
                </div>

                <div style={s.info}>
                    <strong>What happens:</strong><br />
                    • Fetches order history for each order from ShipHero API<br />
                    • Extracts the order import date (earliest history entry)<br />
                    • Flags orders with the "locations disabled" message<br />
                    • Flagged orders are fed into the Watchdog pipeline for validation
                </div>

                <button
                    style={s.button(isRunning || starting)}
                    onClick={handleStart}
                    disabled={isRunning || starting}
                >
                    {starting ? "Starting..." : isRunning ? "Scan Running..." : "🚀 Start History Scan"}
                </button>

                {error && <div style={s.alert}>⚠️ {error}</div>}
            </div>

            {jobId && (
                <div style={s.container}>
                    <div style={{ ...s.title, marginBottom: "16px" }}>Job Progress</div>
                    <JobProgress jobId={jobId} />
                </div>
            )}
        </div>
    );
}
